import type { Category, Expense } from '../api';
import { formatMonthTitle } from './dates';

export type MonthGroup = {
  key: string;
  label: string;
  total: number;
  items: Expense[];
};

function expenseSortKey(ex: Expense): string {
  const d = ex.date || '';
  const t = ex.time || '00:00';
  return `${d}T${t}`;
}

function compareExpensesDesc(a: Expense, b: Expense): number {
  const cmp = expenseSortKey(b).localeCompare(expenseSortKey(a));
  if (cmp !== 0) return cmp;
  return (b.createdAt || '').localeCompare(a.createdAt || '');
}

export function buildMonthGroups(
  expenses: Expense[],
  categoryById: Map<string, Category>,
  selectedIds: Set<string>
): MonthGroup[] {
  const filtered = expenses.filter((ex) => {
    if (!selectedIds.has(ex.categoryId)) return false;
    const cat = categoryById.get(ex.categoryId);
    return cat?.type === 'out';
  });

  const byMonth = new Map<string, Expense[]>();
  for (const ex of filtered) {
    const key = (ex.date || '').slice(0, 7);
    if (!key || key.length < 7) continue;
    const list = byMonth.get(key);
    if (list) list.push(ex);
    else byMonth.set(key, [ex]);
  }

  const groups: MonthGroup[] = [];
  for (const [key, items] of byMonth) {
    const [ys, ms] = key.split('-');
    const year = Number(ys);
    const monthIndex = Number(ms) - 1;
    if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) continue;
    items.sort(compareExpensesDesc);
    const total = items.reduce((sum, ex) => sum + (Number(ex.amount) || 0), 0);
    groups.push({
      key,
      label: formatMonthTitle(year, monthIndex),
      total,
      items,
    });
  }

  groups.sort((a, b) => b.key.localeCompare(a.key));
  return groups;
}
